import Validate from 'components/hoc/Validate';
import BaseInput from 'components/inputs/BaseInput';
import Label from 'components/inputs/Label';
import ValidatableInput from 'components/inputs/ValidatableInput';
import AppStoreContext, { AppStore } from 'contexts/AppStoreContext';
import React, { useState, useContext } from 'react';
import { User } from 'records/User';
import { RawUserProfile, UserProfile } from 'records/UserProfile';
import { IsNotEmpty } from 'utils/Validate';
import { product } from 'copy/copy';
import { useTranslator } from '@bluebeam/react-i18n';
import { ModalStaticProperties } from 'components/ModalPortal/ModalPortal';

interface Props {
  user: User;
}

const EnterUserProfileInfoModal: React.FC<Props> & ModalStaticProperties = (
  props
) => {
  const [profile, setProfile] = useState<UserProfile>(
    props.user.profile.set('additionalinfoneeded', false)
  );

  const { translate } = useTranslator(['userProfile', 'buttons', 'formLabels']);

  const { actions } = useContext(AppStoreContext);

  const validators = [
    {
      name: 'required',
      message: translate('userProfile:requiredValue'),
      test: (value: string): boolean => {
        return IsNotEmpty(value);
      },
    },
  ];

  const handleChange = (
    fieldName: keyof RawUserProfile,
    value: string
  ): void => {
    setProfile(profile.set(fieldName, value));
  };

  const handleCreateUserProfile = async (
    evt: React.FormEvent
  ): Promise<void> => {
    evt.preventDefault();

    const { createUserProfile, clearAllModals } = actions;

    await createUserProfile(profile);

    clearAllModals();
  };

  const { user } = props;

  const isValid =
    profile.firstname && profile.lastname && profile.company && profile.role;

  const productName = product?.productName;

  return (
    <div id="enter-user-profile-info-modal">
      <div className="bgd-gray-2 hs3 row items-center px3">
        <div className="font-size-6">
          {translate('userProfile:welcome', { productName })}
        </div>
      </div>
      <div className="m3">
        <div className="mb4">{translate('userProfile:getStarted')}</div>
        <form onSubmit={handleCreateUserProfile}>
          <div className="column">
            <div className="mb4">
              <Label
                label={translate('formLabels:firstName')}
                required
                requiredText={translate('formLabels:allFieldsRequired')}
              />
              <Validate validators={validators}>
                {({
                  valid,
                  onBlur,
                  onFocus,
                  onChange,
                  onLoad,
                  tested,
                }): React.ReactNode => (
                  <ValidatableInput
                    id="profile-info-modal-firstname"
                    type="text"
                    name="firstname"
                    value={profile.firstname}
                    onChange={(evt): void => {
                      handleChange('firstname', evt.target.value);
                      onChange(evt);
                    }}
                    isValid={valid}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onInputLoad={onLoad}
                    tested={tested}
                  />
                )}
              </Validate>
            </div>
            <div className="mb4">
              <Label label={translate('formLabels:lastName')} />
              <Validate validators={validators}>
                {({
                  valid,
                  onBlur,
                  onFocus,
                  onChange,
                  onLoad,
                  tested,
                }): React.ReactNode => (
                  <ValidatableInput
                    id="profile-info-modal-lastname"
                    type="text"
                    name="lastname"
                    value={profile.lastname}
                    onChange={(evt): void => {
                      handleChange('lastname', evt.target.value);
                      onChange(evt);
                    }}
                    isValid={valid}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onInputLoad={onLoad}
                    tested={tested}
                  />
                )}
              </Validate>
            </div>
            <div className="mb4">
              <Label label={translate('formLabels:email')} />
              <BaseInput
                id="profile-info-modal-email"
                name="email"
                value={user.email}
                disabled
              />
            </div>
            <div className="mb4">
              <Label label={translate('formLabels:company')} />
              <Validate validators={validators}>
                {({
                  valid,
                  onBlur,
                  onFocus,
                  onChange,
                  onLoad,
                  tested,
                }): React.ReactNode => (
                  <ValidatableInput
                    id="profile-info-modal-company"
                    type="text"
                    name="company"
                    value={profile.company}
                    onChange={(evt): void => {
                      handleChange('company', evt.target.value);
                      onChange(evt);
                    }}
                    isValid={valid}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onInputLoad={onLoad}
                    tested={tested}
                  />
                )}
              </Validate>
            </div>
            <div className="mb7">
              <Label label={translate('formLabels:jobTitle')} />
              <Validate validators={validators}>
                {({
                  valid,
                  onBlur,
                  onFocus,
                  onChange,
                  onLoad,
                  tested,
                }): React.ReactNode => (
                  <ValidatableInput
                    id="profile-info-modal-role"
                    type="text"
                    name="role"
                    value={profile.role}
                    onChange={(evt): void => {
                      handleChange('role', evt.target.value);
                      onChange(evt);
                    }}
                    isValid={valid}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onInputLoad={onLoad}
                    tested={tested}
                  />
                )}
              </Validate>
            </div>
            <div className="row justify-end">
              <button
                id="profile-info-modal-confirm-btn"
                className="btn-lg btn-primary"
                type="submit"
                disabled={!isValid}
              >
                {translate('buttons:confirm')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

EnterUserProfileInfoModal.modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    top: 'calc(50vh - 350px)',
    left: 'calc(50vw - 270px)',
    bottom: 'initial',
    right: 'initial',
    width: '540px',
    padding: '0px',
  },
};

export default EnterUserProfileInfoModal;
